import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';

const TransactionsReportsTab = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Dummy static data
  const transactions = [
    {
      id: 'tx1',
      transaction_ref: 'TRX-001',
      customer: 'John Doe',
      unit: 'Unit A',
      payment_method: 'Mpesa',
      total_amount: 1500.5,
      status: 'paid',
      added_on: '2024-07-01T10:00:00Z'
    },
    {
      id: 'tx2',
      transaction_ref: 'TRX-002',
      customer: 'Jane Smith',
      unit: 'Unit B',
      payment_method: 'Cash',
      total_amount: 300.0,
      status: 'unpaid',
      added_on: '2024-07-02T14:30:00Z'
    }
  ];

  const filteredTransactions = transactions.filter((item) => {
    const query = searchTerm.toLowerCase();
    return (
      item.transaction_ref.toLowerCase().includes(query) ||
      item.customer.toLowerCase().includes(query) ||
      item.payment_method.toLowerCase().includes(query) ||
      item.unit.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';

      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount || 0);

  const statusBodyTemplate = (rowData) => {
    const statusColors = {
      paid: 'success',
      partial: 'warning',
      unpaid: 'danger',
      pending: 'info'
    };

    const status = rowData.status?.toLowerCase() || 'pending';
    const color = statusColors[status] || 'secondary';

    return (
      <span className={`badge bg-${color}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const paymentMethodTemplate = (rowData) => {
    const methodColors = {
      cash: 'success',
      mpesa: 'info',
      bank: 'primary',
      card: 'warning'
    };

    const method = rowData.payment_method?.toLowerCase() || 'n/a';
    const color = methodColors[method] || 'secondary';

    return (
      <span className={`badge bg-${color}`}>
        {rowData.payment_method || 'N/A'}
      </span>
    );
  };

  const actionTemplate = (rowData) => (
    <div className="d-flex gap-2">
      <Button
        icon="ti ti-eye"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => handleViewDetails(rowData)}
        tooltip="View Details"
      />
    </div>
  );

  const handleViewDetails = (transactionData) => {
    navigate(`/core/water_meter_management/view_transaction/${transactionData.id}`, {
      state: { transaction: transactionData }
    });
  };

  return (
    <div className="card">
      <div className="card-body">
        {/* Search Input */}
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="ti ti-search"></i>
              </span>
              <InputText
                placeholder="Search by transaction ref, customer, method..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="dt-responsive table-responsive">
          <DataTable
            value={filteredTransactions}
            emptyMessage="No transactions found."
            sortMode="multiple"
            paginator
            rows={10}
            stripedRows
            tableStyle={{ minWidth: '50rem' }}
            stateStorage="session"
            stateKey="water-transactions-state"
          >
            <Column field="transaction_ref" header="Transaction Ref" sortable />
            <Column field="customer" header="Customer" sortable />
            <Column field="unit" header="Unit No" sortable />
            <Column
              field="payment_method"
              header="Payment Method"
              body={paymentMethodTemplate}
              sortable
            />
            <Column
              field="total_amount"
              header="Total Amount"
              body={(rowData) => formatCurrency(rowData.total_amount)}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
            />
            <Column
              field="added_on"
              header="Added On"
              body={(rowData) => formatDate(rowData.added_on)}
              sortable
            />
            <Column
              header="Actions"
              body={actionTemplate}
              style={{ width: '8rem' }}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default TransactionsReportsTab;
